import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Upload, Trash2, ArrowRight } from 'lucide-react';
import ColorPicker from './ColorPicker';
import VectorizeModal from './VectorizeModal';
import PriceBreakdownModal from './PriceBreakdownModal';
import TextEditor from './TextEditor';
import { formatPrice } from '../../utils/formatPrice';
import { logger } from '../../utils/logger';
import RemoveGraphicModal from './RemoveGraphicModal';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Separator } from '../ui/separator';
import { Item, ItemMedia, ItemContent, ItemTitle, ItemDescription, ItemActions } from '../ui/item';
import { Card, CardContent } from '../ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { removeBackground, hasSolidBackground } from '../../utils/backgroundRemoval';
import ShapeDrawer from './ShapeDrawer';

const truncateFilename = (filename, maxLength = 25) => {
  if (!filename || filename.length <= maxLength) return filename;
  
  const extension = filename.split('.').pop();
  const nameWithoutExt = filename.slice(0, -(extension.length + 1));
  
  const halfLength = Math.floor((maxLength - 3) / 2);
  const start = nameWithoutExt.slice(0, halfLength);
  const end = nameWithoutExt.slice(-halfLength);
  
  return `${start}...${end}.${extension}`;
};

const GraphicsSidebar = ({ 
  uploadedFiles, 
  handleFileUpload, 
  placeGraphic, 
  removeUploadedFile, 
  selectedColor, 
  setSelectedColor, 
  helfoliering,
  setHelfoliering,
  helfolieringFee = 10000,
  price,
  placedGraphics,
  onAddText,
  selectedText,
  onUpdateText,
  activeShapeTool,
  shapeProperties,
  selectedShape,
  onShapeToolSelect,
  onShapePropertyChange,
  onNextStep,
  setPlacedGraphics,
  onFileCheckReady
}) => {
  const fileInputRef = useRef(null);
  const [modalFile, setModalFile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPriceModalOpen, setIsPriceModalOpen] = useState(false);
  const [showTextEditor, setShowTextEditor] = useState(false);
  const [activeTab, setActiveTab] = useState('graphics');
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [fileToRemove, setFileToRemove] = useState(null);
  const [isProcessingBackground, setIsProcessingBackground] = useState(false);
  const [hasBackground, setHasBackground] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  // Helper to save color to recent colors
  const handleColorChange = (color) => {
    setSelectedColor(color);
    // Save to recent colors
    const stored = localStorage.getItem('dekalio_recent_colors');
    let recentColors = stored ? JSON.parse(stored) : [];
    recentColors = [color.toUpperCase(), ...recentColors.filter(c => c.toLowerCase() !== color.toLowerCase())].slice(0, 5);
    localStorage.setItem('dekalio_recent_colors', JSON.stringify(recentColors));
  };

  useEffect(() => {
    logger.log('GraphicsSidebar placedGraphics:', placedGraphics);
  }, [placedGraphics]);

  useEffect(() => {
    logger.log('Selected text in GraphicsSidebar:', {
      selectedText,
      showTextEditor
    });
    if (selectedText) {
      setShowTextEditor(true);
    }
  }, [selectedText]);

  useEffect(() => {
    if (selectedText) {
      setActiveTab('text');
    }
  }, [selectedText]);

  const handleFileCheck = useCallback(async (file) => {
    if (file.type === 'image/svg+xml') {
      // SVG files can be uploaded and placed directly
      handleFileUpload(file).then(uploadedFile => {
        if (uploadedFile) {
          placeGraphic(uploadedFile); // Auto-place the graphic after upload
        }
      });
    } else if (['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      // Check if image has solid background
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const imageUrl = e.target.result;
          
          // Detect if has solid background
          const hasBg = await hasSolidBackground(imageUrl);
          
          setHasBackground(hasBg);
          setPreviewImage(hasBg ? imageUrl : null);
          setModalFile(file);
          setIsModalOpen(true);
        } catch (error) {
          logger.error('Error detecting background:', error);
          // Show modal anyway
          setHasBackground(false);
          setPreviewImage(null);
          setModalFile(file);
          setIsModalOpen(true);
        }
      };
      reader.readAsDataURL(file);
    } else {
      alert('Please upload a valid SVG, PNG, or JPG file.');
    }
  }, [handleFileUpload, placeGraphic]);

  // Expose handleFileCheck to parent component (must be after handleFileCheck is defined)
  useEffect(() => {
    if (onFileCheckReady) {
      onFileCheckReady(handleFileCheck);
    }
  }, [onFileCheckReady, handleFileCheck]);

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileCheck(e.target.files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileCheck(e.dataTransfer.files[0]);
    }
  };

  const handleContinueWithVectorization = async (shouldRemoveBackground = false) => {
    if (modalFile) {
      try {
        setIsProcessingBackground(true);
        
        // If user chose to remove background and we detected one
        if (shouldRemoveBackground && hasBackground) {
          // Convert file to data URL
          const reader = new FileReader();
          reader.onload = async (e) => {
            try {
              logger.log('Removing background from image...');
              
              // Remove background
              const processedImageUrl = await removeBackground(e.target.result, {
                tolerance: 30,
                autoDetect: true
              });
              
              logger.log('Background removed successfully');
              
              // Convert the processed image back to a File object
              const response = await fetch(processedImageUrl);
              const blob = await response.blob();
              const processedFile = new File(
                [blob], 
                modalFile.name, 
                { type: 'image/png' }
              );
              
              // Upload the processed file
              const uploadedFile = await handleFileUpload(processedFile, true);
              if (uploadedFile) {
                placeGraphic(uploadedFile); // Auto-place the vectorized graphic after upload
              }
              setIsModalOpen(false);
              setModalFile(null);
              setHasBackground(false);
              setPreviewImage(null);
              setIsProcessingBackground(false);
            } catch (error) {
              logger.error('Error processing image:', error);
              // Fallback to original file if processing fails
              const uploadedFile = await handleFileUpload(modalFile, true);
              if (uploadedFile) {
                placeGraphic(uploadedFile);
              }
              setIsModalOpen(false);
              setModalFile(null);
              setHasBackground(false);
              setPreviewImage(null);
              setIsProcessingBackground(false);
            }
          };
          reader.readAsDataURL(modalFile);
        } else {
          // Upload without background removal
          const uploadedFile = await handleFileUpload(modalFile, true);
          if (uploadedFile) {
            placeGraphic(uploadedFile);
          }
          setIsModalOpen(false);
          setModalFile(null);
          setHasBackground(false);
          setPreviewImage(null);
          setIsProcessingBackground(false);
        }
      } catch (error) {
        logger.error('Error in handleContinueWithVectorization:', error);
        setIsModalOpen(false);
        setModalFile(null);
        setHasBackground(false);
        setPreviewImage(null);
        setIsProcessingBackground(false);
      }
    }
  };

  const handleRequestQuote = () => {
    logger.log('Quote requested for file:', modalFile?.name);
    setIsModalOpen(false);
    setModalFile(null);
  };

  const handleRemoveFile = (file) => {
    setFileToRemove(file);
    setRemoveModalOpen(true);
  };

  const confirmRemove = () => {
    if (!fileToRemove) return;

    // Remove the file from uploadedFiles
    removeUploadedFile(fileToRemove.id);

    // Remove all instances of graphics using this file from placedGraphics
    setPlacedGraphics(prev => {
      const newPlacedGraphics = {};
      Object.keys(prev).forEach(view => {
        newPlacedGraphics[view] = prev[view].filter(
          graphic => graphic.originalFileId !== fileToRemove.id
        );
      });
      return newPlacedGraphics;
    });

    setFileToRemove(null);
  };

  return (
    <>
      <aside className="w-64 flex flex-col sidebar-graphics bg-white h-full relative border-l border-gray-200">
        <div className="flex-1 overflow-y-auto pb-[180px]">
          <div className="p-4 space-y-6">
          <Tabs 
            value={activeTab} 
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="w-full grid grid-cols-3">
              <TabsTrigger value="graphics">Grafik</TabsTrigger>
              <TabsTrigger value="text" className="text-joyride">
                {selectedText ? 'Redigera Text' : 'Text'}
              </TabsTrigger>
              <TabsTrigger value="shapes">Former</TabsTrigger>
            </TabsList>

            <TabsContent value="shapes" className="pt-4">
              <ShapeDrawer
                activeShapeTool={activeShapeTool}
                onShapeToolSelect={onShapeToolSelect}
                shapeProperties={shapeProperties}
                onShapePropertyChange={onShapePropertyChange}
                selectedGraphic={selectedShape}
              />
            </TabsContent>

            <TabsContent value="text" className="pt-4">
            <TextEditor
              onTextAdd={onAddText}
              selectedText={selectedText}
              onTextUpdate={(textConfig) => {
                console.log('TextEditor update:', textConfig);
                if (selectedText) {
                  onUpdateText({
                    ...textConfig,
                    id: selectedText.id
                  });
                } else {
                  onAddText(textConfig);
                }
              }}
            />
          </TabsContent>

          <TabsContent value="graphics" className="pt-4 space-y-2">
            <Item 
              variant="outline"
              className="cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={handleClick}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              data-tour="upload-graphic"
            >
              <ItemMedia variant="icon">
                <Upload />
              </ItemMedia>
              <ItemContent>
                <ItemTitle>Ladda upp grafik</ItemTitle>
                <ItemDescription>SVG, PNG eller JPG</ItemDescription>
              </ItemContent>
            </Item>
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleFileInputChange} 
              accept=".svg,.png,.jpg,.jpeg" 
              className="hidden" 
            />
            
            {uploadedFiles.length > 0 && (
              <div className="space-y-2 pt-2">
                <TooltipProvider>
                  {uploadedFiles.map((file) => (
                    <Item key={file.id} variant="outline" size="sm">
                      <ItemMedia variant="image">
                        <img src={file.src} alt={file.name} className="size-10 object-contain" />
                      </ItemMedia>
                      <ItemContent>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <ItemTitle className="text-sm cursor-help">{truncateFilename(file.name)}</ItemTitle>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{file.name}</p>
                          </TooltipContent>
                        </Tooltip>
                      </ItemContent>
                      <ItemActions>
                        <Button 
                          onClick={() => placeGraphic(file)} 
                          size="sm"
                        >
                          Placera
                        </Button>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              onClick={() => handleRemoveFile(file)}
                              size="icon"
                              variant="outline"
                              className="h-8 w-8"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Ta bort grafik</p>
                          </TooltipContent>
                        </Tooltip>
                      </ItemActions>
                    </Item>
                  ))}
                </TooltipProvider>
              </div>
            )}
          </TabsContent>
        </Tabs>

          {activeTab === 'graphics' && (
            <>
              <Separator />

              <div className="color-picker-section">
                <h3 className="text-base font-semibold mb-3">Bilfärg</h3>
                <div className="grid grid-cols-5 gap-2">
                  <TooltipProvider>
                    {[
                      { color: '#000000', name: 'Svart', gradient: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%)' },
                      { color: '#FFFFFF', name: 'Vit', gradient: 'linear-gradient(135deg, #FFFFFF 0%, #f5f5f5 50%, #FFFFFF 100%)' },
                      { color: '#C0C0C0', name: 'Silver', gradient: 'linear-gradient(135deg, #d4d4d4 0%, #a8a8a8 50%, #c8c8c8 100%)' },
                      { color: '#3C4043', name: 'Mörkgrå', gradient: 'linear-gradient(135deg, #4a4d50 0%, #2d2f32 50%, #3c4043 100%)' },
                      { color: '#8B8D8F', name: 'Metallicgrå', gradient: 'linear-gradient(135deg, #9a9c9e 0%, #7a7c7e 50%, #8b8d8f 100%)' },
                      { color: '#1A3B5D', name: 'Mörkblå', gradient: 'linear-gradient(135deg, #244a6f 0%, #152e47 50%, #1a3b5d 100%)' },
                      { color: '#C1282D', name: 'Röd', gradient: 'linear-gradient(135deg, #d63841 0%, #a71f24 50%, #c1282d 100%)' },
                      { color: '#2C3F2E', name: 'Mörkgrön', gradient: 'linear-gradient(135deg, #384d3a 0%, #1f2b20 50%, #2c3f2e 100%)' },
                      { color: '#8A7968', name: 'Beige', gradient: 'linear-gradient(135deg, #9a8978 0%, #7a6958 50%, #8a7968 100%)' }
                    ].map(({ color, name, gradient }) => (
                      <Tooltip key={color}>
                        <TooltipTrigger asChild>
                          <button
                            className={`w-9 h-9 rounded-full transition-all border border-black/20 ${
                              selectedColor === color ? 'ring-2 ring-primary ring-offset-2' : 'hover:scale-110'
                            }`}
                            style={{ background: gradient }}
                            onClick={() => handleColorChange(color)}
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{name}</p>
                        </TooltipContent>
                      </Tooltip>
                    ))}
                    <ColorPicker color={selectedColor} onChange={handleColorChange} />
                  </TooltipProvider>
                </div>
                <div className="flex items-center justify-between gap-3 pt-3">
                  <Label htmlFor="helfoliering" className="text-sm font-medium cursor-pointer flex-1">
                    Helfoliering
                  </Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                      + {formatPrice(helfolieringFee)} kr
                    </span>
                    <Switch
                      id="helfoliering"
                      checked={helfoliering}
                      onCheckedChange={setHelfoliering}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground pt-1">
                  {helfoliering
                    ? 'Vi byter hela fordonsfärgen med folie (ingen lackering).'
                    : 'Byta hela fordonsfärgen med folie istället för lackering.'}
                </p>
              </div>
            </>
          )}
          </div>
        </div>

        {/* Fixed Bottom Summary */}
        <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-border">
          <Card className="sidebar-summary bg-white shadow-none border-0 rounded-none">
            <CardContent className="p-4 space-y-3">
              {(Object.values(placedGraphics).flat().length > 0 || helfoliering) && (
                <Button 
                  onClick={() => setIsPriceModalOpen(true)}
                  variant="link"
                  className="w-full justify-start p-0 h-auto text-sm"
                >
                  Prisuträkning
                </Button>
              )}
              
              <div>
                <span className="text-sm text-muted-foreground block">Totalt inkl moms</span>
                <span className="text-2xl font-bold block">{formatPrice(price)} kr</span>
              </div>

              <Button 
                className="w-full"
                onClick={onNextStep}
                size="lg"
                disabled={Object.values(placedGraphics).flat().length === 0 && !helfoliering}
              >
                Gå vidare
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </CardContent>
          </Card>
        </div>
      </aside>

      <VectorizeModal 
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setModalFile(null);
          setHasBackground(false);
          setPreviewImage(null);
          setIsProcessingBackground(false);
        }}
        onContinue={handleContinueWithVectorization}
        onRequestQuote={handleRequestQuote}
        filename={modalFile?.name}
        isProcessing={isProcessingBackground}
        hasBackground={hasBackground}
        previewImage={previewImage}
      />

      <PriceBreakdownModal 
        isOpen={isPriceModalOpen}
        onClose={() => setIsPriceModalOpen(false)}
        placedGraphics={placedGraphics}
        helfoliering={helfoliering}
        helfolieringFee={helfolieringFee}
      />

      <RemoveGraphicModal
        isOpen={removeModalOpen}
        onClose={() => setRemoveModalOpen(false)}
        onConfirm={confirmRemove}
        filename={fileToRemove?.name}
      />
    </>
  );
};

export default GraphicsSidebar;